/**
 * Комнаты: превращаем состояние MatrixClient в плоский список для UI.
 *
 * Почему отдельный модуль, а не логика в компоненте: список комнат надо
 * пересобирать на каждое событие sync, и React-компонент не должен знать,
 * как SDK хранит таймлайны. Здесь — только чистые функции над клиентом.
 */
import {
  EventType,
  NotificationCountType,
  type MatrixClient,
  type MatrixEvent,
  type Room,
} from "matrix-js-sdk";
import { tr } from "../i18n";
import { displayBody } from "./mentions";

export interface RoomSummary {
  roomId: string;
  name: string;
  /** Инициалы для заглушки аватара, когда картинки нет. */
  initials: string;
  /**
   * mxc://-адрес аватара или null. НЕ http-ссылка: наш сервер отдаёт media
   * только с токеном, поэтому картинку грузит useAuthedAvatar (media.ts).
   */
  avatarUrl: string | null;
  /**
   * Автор последнего сообщения для префикса превью: «Вы» / «Имя» / null.
   * null — когда префикс не нужен (личная переписка, чужое сообщение).
   */
  previewSender: string | null;
  /** Текст последнего сообщения в одну строку. */
  previewText: string;
  /** Время последнего события комнаты, мс. 0 — если событий нет. */
  lastTs: number;
  /**
   * Непрочитанное ЛЮБОГО вида (серверный счётчик + локальная поправка,
   * см. unreadOf). Поле НЕ тронуто моделью 13:49 и нужно само по себе:
   * из него выводится hasNewMessages (шум → жирная строка) и отбор
   * вкладки «Непрочитанные». Бейджи строки и навигации его не читают.
   */
  unread: number;
  /**
   * МОДЕЛЬ НЕПРОЧИТАННОГО (Антон, 2026-09-10 13:49 — отменяет «считать всё»):
   * циферка означает «меня ждут», а не «сколько пришло». Сюда попадает
   * ТОЛЬКО одно из трёх (см. notifyingOf):
   *   1) любое непрочитанное в личной комнате 1х1;
   *   2) в групповой — только упоминания меня (highlight);
   *   3) непрочитанные ответы в тредах, КОРЕНЬ которых отправлен мной.
   * Все агрегированные счётчики (рельс, Home, фильтры, заголовок вкладки)
   * считают ТОЛЬКО это поле — правило сложное и живёт в ОДНОМ месте.
   */
  notifying: number;
  /**
   * «Есть что-то новое» — строка рисуется ЖИРНЫМ, без циферки. true, когда
   * сервер видит непрочитанное, но по модели оно мне не адресовано
   * (обычное сообщение в группе без упоминания). Шум не должен выглядеть
   * как адресованное лично — это и есть смысл разделения notifying/hasNew.
   */
  hasNewMessages: boolean;
  /** true — комната зашифрована; такие мы намеренно не читаем (см. isEncrypted). */
  encrypted: boolean;
  /** true — это приглашение, а не присоединённая комната. */
  invite: boolean;
  /** true — личная переписка (DM). Источник — m.direct, см. directRoomIds. */
  direct: boolean;
  /** true — чат в избранном (тег m.favourite). Такие идут отдельной секцией сверху. */
  pinned: boolean;
  /** id пространств, в которые входит комната. Пусто — комната вне пространств. */
  spaceIds: string[];
  /**
   * Сколько непрочитанного приходится на ТРЕДЫ (MSC3440), внутри общего `unread`.
   * Нужно, чтобы строка комнаты не показывала ноль, когда непрочитано только
   * в треде — см. unreadOf и раздел про треды в отчёте.
   */
  threadUnread: number;
  /**
   * Счётчик упоминаний (highlight). Сейчас показывается красным бейджем;
   * своя подсветка в ленте — следующий этап.
   */
  highlight: number;
}

/** Пространство (m.space) — вкладка в ряду И иконка в вертикальном рельсе. */
export interface SpaceInfo {
  roomId: string;
  name: string;
  /**
   * ОДНА буква для круглой заглушки иконки спейса, когда аватара нет
   * (правка Антона 2026-09-21: «если аватара нет — аккуратный кружок с
   * одной буквой, а не сырой текстовый блок»; двухбуквенные инициалы
   * «AL»/«FA» на круге 40px выглядели текстовой плашкой).
   */
  initials: string;
  /** mxc://-адрес аватара пространства или null (грузится с авторизацией). */
  avatarUrl: string | null;
  /**
   * Число чатов внутри пространства, которые МЕНЯ ЖДУТ (notifying > 0).
   * По модели 13:49 единица навигации — ЧАТ, а не сообщение: это и есть
   * циферка на иконке спейса и на его вкладке. Суммы сообщений в
   * навигации НЕ считаем нигде (финальное уточнение Антона, 13:49);
   * число сообщений живёт только в строке конкретной комнаты.
   */
  unreadRooms: number;
  /**
   * Число чатов пространства, где меня УПОМИНУЛИ (highlight > 0).
   * Красный бейдж — тоже ЧАТЫ, а не сумма упоминаний (модель 13:49).
   */
  highlight: number;
}

/**
 * Зашифрована ли комната.
 *
 * Решение Антона: E2EE в этом клиенте ВЫКЛЮЧЕН намеренно, crypto не
 * инициализируется. Поэтому нельзя пользоваться room.hasEncryptionStateEvent()
 * из crypto-слоя — мы читаем состояние комнаты напрямую: наличие
 * m.room.encryption в state необратимо и видно без crypto.
 */
export function isEncrypted(room: Room): boolean {
  return Boolean(room.currentState.getStateEvents(EventType.RoomEncryption, ""));
}

/**
 * Множество id личных комнат (DM).
 *
 * ЕДИНСТВЕННЫЙ честный источник — account data `m.direct`: это тот самый
 * список, который ведут Element и все прочие клиенты. Ни `is_direct` из
 * createRoom, ни «в комнате двое» источником быть не могут:
 *   - `is_direct` живёт только в приглашении и в состоянии комнаты НЕ остаётся;
 *   - «двое участников» — это ровно так же выглядит групповая комната, из
 *     которой все вышли, и наоборот: DM с третьим приглашённым перестал бы
 *     считаться личным.
 * Поэтому комнату, которой нет в m.direct, мы личной не объявляем — лучше
 * не показать в «Личных», чем соврать. Ограничение описано в отчёте.
 */
export function directRoomIds(client: MatrixClient): Set<string> {
  const ids = new Set<string>();
  const content = client.getAccountData(EventType.Direct)?.getContent<
    Record<string, string[] | undefined>
  >();
  if (!content) return ids;
  for (const list of Object.values(content)) {
    if (!Array.isArray(list)) continue;
    for (const roomId of list) if (typeof roomId === "string") ids.add(roomId);
  }
  return ids;
}

/**
 * В избранном ли чат.
 *
 * ШТАТНЫЙ тег `m.favourite` (spec: Room Tagging) — ровно тот механизм, которым
 * Element наполняет раздел «Избранное». Его и берём: это настоящее состояние
 * на сервере, общее для всех клиентов, а не выдуманный локальный флаг.
 * Произвольные пользовательские теги СОЗНАТЕЛЬНО не изобретаем: клиент,
 * придумавший свой тег, рассинхронизируется с Element и с самим собой на
 * другом устройстве.
 *
 * ОГРАНИЧЕНИЕ (не закрыто этим этапом): наш клиент тег только ЧИТАЕТ.
 * Поставить/снять избранное из UI пока нельзя — нет контекстного меню строки.
 * Пока его нет, вкладка «Избранное» наполняется только из Element.
 */
export function isPinned(room: Room): boolean {
  return Boolean(room.tags?.[FAVOURITE_TAG]);
}

/** Имя штатного тега избранного. Одно место, чтобы не разъехалось по коду. */
export const FAVOURITE_TAG = "m.favourite";

/**
 * Карта «комната -> в каких пространствах состоит».
 *
 * Состав пространства задаётся событиями состояния `m.space.child` В САМОМ
 * пространстве. Обратную связь (`m.space.parent` в комнате) намеренно НЕ
 * используем как основной источник: её ставят не все клиенты, она
 * необязательна по спеке, и построенный на ней список был бы неполным.
 *
 * Вложенные пространства разворачиваем: комната из подпространства должна
 * попадать и во вкладку родителя — иначе вкладка верхнего уровня выглядела бы
 * полупустой. Глубина ограничена, чтобы цикл (пространство внутри самого себя,
 * что Matrix не запрещает) не увёл обход в бесконечность.
 */
export function spaceMembership(client: MatrixClient): Map<string, Set<string>> {
  const spaces = client.getRooms().filter((r) => r.isSpaceRoom() && r.getMyMembership() === "join");
  const childrenOf = new Map<string, string[]>();
  for (const space of spaces) {
    const kids = space.currentState
      .getStateEvents("m.space.child")
      .filter((ev) => Object.keys(ev.getContent()).length > 0) // пустой content = связь удалена
      .map((ev) => ev.getStateKey())
      .filter((id): id is string => Boolean(id));
    childrenOf.set(space.roomId, kids);
  }

  const result = new Map<string, Set<string>>();
  const add = (roomId: string, spaceId: string) => {
    const cur = result.get(roomId) ?? new Set<string>();
    cur.add(spaceId);
    result.set(roomId, cur);
  };

  for (const space of spaces) {
    const seen = new Set<string>([space.roomId]);
    const queue = [...(childrenOf.get(space.roomId) ?? [])];
    let guard = 0;
    while (queue.length > 0 && guard++ < 5000) {
      const id = queue.shift();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      if (childrenOf.has(id)) {
        queue.push(...(childrenOf.get(id) ?? [])); // подпространство — идём глубже
      } else {
        add(id, space.roomId);
      }
    }
  }
  return result;
}

function initialsOf(name: string): string {
  const words = name
    .replace(/^[#!@]/, "")
    .split(/[\s_\-.]+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * ОДНА буква заглушки пространства (Антон 2026-09-21): круг 40px с одной
 * буквой читается как аватар-заглушка; две буквы («AL», «FA») на маленьком
 * круге сжимались и выглядели «сырым текстовым блоком». Правило то же,
 * что initialsOf для одного слова, но всегда ровно одна буква: берём
 * первую букву первого значимого слова (префиксы #!@ и разделители
 * пропускаем — «ALG-I» → «A», «Family» → «F»). Букв нет вовсе — "?".
 */
function spaceLetterOf(name: string): string {
  const words = name
    .replace(/^[#!@]+/, "")
    .split(/[\s_\-.]+/)
    .filter(Boolean);
  for (const w of words) {
    // Первая БУКВА слова: в имени спейса могут быть скобки/цифры впереди.
    const m = w.match(/[\p{L}\p{N}]/u);
    if (m) return m[0].toUpperCase();
  }
  return "?";
}

/** Текст последнего сообщения комнаты + кто его написал (для префикса). */
function previewOf(
  client: MatrixClient,
  room: Room,
  encrypted: boolean,
  direct: boolean,
): { sender: string | null; text: string } {
  // Зашифрованную комнату мы не расшифровываем (crypto выключен), но пустая
  // строка выглядела бы как «сообщений нет» — это неправда. Говорим прямо.
  if (encrypted) return { sender: null, text: tr("room.encryptedPreview") };

  const timeline = room.getLiveTimeline().getEvents();
  for (let i = timeline.length - 1; i >= 0; i--) {
    const ev = timeline[i];
    // Звонок — тоже событие истории, и если он ПОСЛЕДНИЙ, превью обязано
    // говорить о нём, а не о предыдущем сообщении (Антон 2026-09-17 14:41:
    // «здесь последнее событие звонок, а не моё сообщение»). Раньше время
    // строки брали от звонка (lastTsOf), а текст — от файла: строка лгала.
    if (ev.getType() === EventType.GroupCallMemberPrefix) {
      const content = ev.getContent() as { expires?: number; created_ts?: number };
      const live =
        Object.keys(content).length > 0 &&
        (content.created_ts ?? ev.getTs()) + (content.expires ?? 4 * 3_600_000) > Date.now();
      const text = tr(live ? "timeline.callActive" : "timeline.callFinished");
      // state_key у MatrixRTC-членства — не user ID, а «_@user:server_DEVICE»: в превью
      // он показывался сырым «_@sz:algsucce…» (Антон 2026-09-18 12:21). Автор события —
      // настоящий user ID, берём его.
      const who = ev.getSender() ?? ev.getStateKey();
      const me = who === client.getUserId();
      if (me) return { sender: tr("timeline.senderMe"), text };
      if (direct) return { sender: null, text };
      const member = who ? room.getMember(who) : null;
      return { sender: member?.name ?? who ?? null, text };
    }
    if (ev.getType() !== EventType.RoomMessage) continue;
    const body = String(ev.getContent().body ?? "");
    if (!body) continue;
    const mentionsRaw = ev.getContent()["m.mentions"] as { user_ids?: string[] } | undefined;
    // Себя НЕ фильтруем (Антон 2026-10-09): иначе превью сообщения,
    // адресованного мне («ab: …»), не превращалось в «@ab …».
    const mentions = (mentionsRaw?.user_ids ?? []).map((userId) => ({
      userId,
      displayName: room.getMember(userId)?.name ?? userId,
    }));
    const rawFormatted = ev.getContent().formatted_body;
    const display = displayBody(
      body,
      mentions.length > 0 ? mentions : null,
      typeof rawFormatted === "string" ? rawFormatted : null,
    );
    const text = display.replace(/\s+/g, " ").slice(0, 160);
    const me = ev.getSender() === client.getUserId();
    // Как в Telegram: «Вы:» всегда, имя автора — только в группах.
    // В личной переписке имя собеседника и так написано в заголовке строки,
    // дублировать его в превью значит съесть половину ширины ни на что.
    if (me) return { sender: tr("timeline.senderMe"), text };
    if (direct) return { sender: null, text };
    return { sender: senderLabel(room, ev), text };
  }
  return { sender: null, text: "" };
}

/** Отображаемое имя автора события в контексте комнаты. */
export function senderLabel(room: Room, ev: MatrixEvent): string {
  const sender = ev.getSender();
  if (!sender) return tr("timeline.senderUnknown");
  const member = room.getMember(sender);
  return member?.name ?? sender;
}

/** Время последнего события — по нему сортируем список. */
function lastTsOf(room: Room): number {
  const events = room.getLiveTimeline().getEvents();
  for (let i = events.length - 1; i >= 0; i--) {
    const ts = events[i].getTs();
    if (ts) return ts;
  }
  // Комната без загруженных событий: не роняем сортировку, просто в конец.
  return 0;
}

/**
 * Сколько в комнате непрочитанного.
 *
 * Источник истины — счётчик сервера (`unread_notifications` из sync): только он
 * знает про историю, которую клиент не загружал. Но у него есть задержка: наш
 * m.read улетел, а обновлённый счётчик придёт лишь следующим sync, и человек
 * секунду видит непрочитанное в комнате, которую только что открыл.
 *
 * Поэтому локальная поправка: если мы уже прочитали самое свежее известное
 * событие комнаты — показываем 0, не дожидаясь сервера. hasUserReadEvent
 * учитывает и настоящие отметки, и синтетические (SDK ставит их на наши
 * собственные отправленные сообщения), поэтому попутно выполняется требование
 * «свои сообщения не считаются непрочитанными».
 *
 * Поправка работает только В СТОРОНУ УМЕНЬШЕНИЯ и только когда прочитан именно
 * хвост таймлайна — придумать непрочитанное, которого сервер не видит, она не может.
 */
function unreadOf(client: MatrixClient, room: Room): { total: number; thread: number } {
  // ДЕФЕКТ, НАЙДЕННЫЙ ЗАМЕРОМ НА СТЕНДЕ (подробно — раздел «Треды» в отчёте).
  //
  // Ситуация: в комнате прочитан весь главный таймлайн, но в ТРЕДЕ лежат
  // непрочитанные ответы. Старый код показывал в такой комнате НОЛЬ.
  //
  // Почему так получалось. Ответы в треде matrix-js-sdk убирает из главного
  // таймлайна в отдельный Thread. Локальная поправка ниже смотрела только на
  // хвост ГЛАВНОГО таймлайна: видела «хвост прочитан» и гасила ВЕСЬ счётчик,
  // включая то, что сервер насчитал за тред.
  //
  // ЗАМЕР ПО СЕРВЕРУ (2026-09-09, /_matrix/client/versions нашего Synapse):
  //   org.matrix.msc3440.stable = true   — треды как таковые есть;
  //   org.matrix.msc3773        = false  — ОТДЕЛЬНЫХ тредовых счётчиков НЕТ.
  // То есть на НАШЕМ стенде сервер НЕ присылает unread_thread_notifications,
  // а складывает тредовое в общий notification_count комнаты (проверено
  // сырым /sync: unread_notifications.notification_count = 2,
  // unread_thread_notifications = null). Поэтому чинить надо ОБА случая:
  //
  //  (а) MSC3773 включён (будущий/чужой сервер): считаем комнатную и тредовую
  //      части раздельно и поправку применяем ТОЛЬКО к комнатной;
  //  (б) MSC3773 выключен (наш стенд): тредовое сидит внутри комнатной
  //      части, и единственное, что можно сделать честно — НЕ ГАСИТЬ счётчик
  //      поправкой, пока в тредах есть непрочитанное. Серверу верим,
  //      свою догадку придерживаем.
  const roomPart = room.getRoomUnreadNotificationCount(NotificationCountType.Total) ?? 0;
  const threadPart = (room.getUnreadNotificationCount(NotificationCountType.Total) ?? 0) - roomPart;
  const threads = Math.max(0, threadPart);

  if (roomPart === 0) return { total: threads, thread: threads };

  const me = client.getUserId();
  if (!me) return { total: roomPart + threads, thread: threads };

  // Случай (б): есть тред с непрочитанным последним ответом — поправку НЕ
  // применяем вообще. Thread в SDK 42.3.0 наследует ReadReceipt, у него свой
  // hasUserReadEvent с тредовыми receipts (thread_id) — имена сверены по
  // node_modules/matrix-js-sdk/lib/models/thread.d.ts:181,228.
  if (hasUnreadThreadReplies(room, me)) {
    return { total: roomPart + threads, thread: Math.max(threads, 1) };
  }

  // Поправка на задержку сервера: наш m.read улетел, а обновлённый счётчик
  // придёт лишь следующим sync. Работает только В СТОРОНУ УМЕНЬШЕНИЯ и только
  // когда прочитан именно хвост главного таймлайна.
  const events = room.getLiveTimeline().getEvents();
  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i];
    const id = ev.getId();
    if (!id || id.startsWith("~")) continue;
    // Событие треда в главном таймлайне тоже встречается (корень треда и
    // «висящие» ответы до подгрузки треда). Читать по нему главный таймлайн
    // нельзя — иначе поправка снова начнёт гасить тредовое непрочитанное.
    if (ev.threadRootId && !ev.isThreadRoot) continue;
    const roomRead = room.hasUserReadEvent(me, id) ? 0 : roomPart;
    return { total: roomRead + threads, thread: threads };
  }
  return { total: roomPart + threads, thread: threads };
}

/**
 * Есть ли в комнате тред, последний ответ в котором мы НЕ читали.
 *
 * Смотрим только на ЗАГРУЖЕННЫЕ треды (room.getThreads() — карта в памяти,
 * проверено по lib/models/room.js:1570). Это осознанное ограничение: тред,
 * о котором клиент ещё не знает, сюда не попадёт. Но ошибка при этом
 * безопасная: мы просто применим поправку там, где раньше тоже применяли,
 * то есть хуже чем было не станет.
 */
function hasUnreadThreadReplies(room: Room, me: string): boolean {
  for (const thread of room.getThreads()) {
    const last = thread.lastReply();
    const id = last?.getId();
    if (!id || id.startsWith("~")) continue;
    if (last?.getSender() === me) continue; // своё сообщение непрочитанным не бывает
    if (!thread.hasUserReadEvent(me, id)) return true;
  }
  return false;
}

/**
 * Счётчик упоминаний (highlight). Отдельно от общего непрочитанного, потому что
 * в мессенджерах это разные вещи: 200 сообщений в шумном чате и одно, где
 * назвали по имени. Как и total, суммируем комнату и треды.
 */
function highlightOf(room: Room): number {
  return room.getUnreadNotificationCount(NotificationCountType.Highlight) ?? 0;
}

/**
 * Сколько непрочитанных ответов лежит в МОИХ тредах этой комнаты.
 *
 * Пункт 3 модели (13:49): ответ в треде, корень которого отправлен мной,
 * считается адресованным мне, даже без упоминания. Серверного счётчика под
 * это нет (на нашем стенде нет даже MSC3773 — см. unreadOf), поэтому считаем
 * сами по ЗАГРУЖЕННЫМ тредам — то же осознанное ограничение, что у
 * hasUnreadThreadReplies: тред, которого клиент ещё не видел, сюда не
 * попадёт, и это безопасная сторона ошибки (не сочтём лишнего).
 *
 * Как считаем внутри треда: берём мою последнюю тредовую отметку
 * (thread.getReadReceiptForUserId — Thread наследует ReadReceipt, тип
 * WrappedReceipt.data.ts сверен по lib/@types/read_receipts.d.ts v42.3.0)
 * и считаем ЧУЖИЕ сообщения позже неё. Отметки нет — непрочитано всё
 * чужое. Свои ответы не считаются никогда.
 */
function myThreadsUnread(room: Room, me: string): number {
  let total = 0;
  for (const thread of room.getThreads()) {
    const root = thread.rootEvent;
    if (!root || root.getSender() !== me) continue;
    const receiptTs = thread.getReadReceiptForUserId(me)?.data.ts ?? 0;
    for (const ev of thread.timeline) {
      if (ev.getSender() === me) continue;
      if (ev.getTs() <= receiptTs) continue;
      // Локальное эхо чужого сообщения не бывает, а вот системные события
      // в треде бывают редко, но циферку им давать не надо.
      if (ev.getType() !== EventType.RoomMessage) continue;
      total++;
    }
  }
  return total;
}

/**
 * МОДЕЛЬ НЕПРОЧИТАННОГО — «меня ждут», а не «сколько пришло».
 *
 * Единственное место, где живёт правило из трёх пунктов (Антон, 13:49):
 *   1) direct 1х1 — любое непрочитанное;
 *   2) групповая — только упоминания меня (highlight, серверный счётчик);
 *   3) ответы в тредах, корень которых мой.
 * Всё остальное — жирная строка без циферки (hasNewMessages, см. ниже).
 *
 * Единый источник обязателен: правило нетривиальное, и две его копии в
 * разных файлах разошлись бы на первом же пограничном случае (прямое
 * требование постановки). UI получает готовое число и не пересчитывает.
 */
function notifyingOf(
  client: MatrixClient,
  room: Room,
  direct: boolean,
  total: number,
): number {
  const me = client.getUserId();
  if (!me) return 0;
  // (1) Личная переписка: меня ждёт любое непрочитанное сообщение.
  if (direct) return total;
  // (2) + (3) Групповая: упоминания + ответы в моих тредах. Складываем,
  // сознавая ПОГРАНИЧНЫЙ СЛУЧАЙ: упоминание ВНУТРИ моего треда сервер
  // включит и в highlight, и мы его же сочтём в myThreadsUnread — двойной
  // счёт на одно сообщение. Разделять их точно (вычитать тредовые
  // упоминания из highlight) на сервере без MSC3773 невозможно, а пропустить
  // адресованное хуже, чем сосчитать дважды. Выбор сознательный.
  return highlightOf(room) + myThreadsUnread(room, me);
}

/**
 * Адрес аватара в виде mxc://, А НЕ готовый http-URL (правка 2026-09-13).
 *
 * Раньше здесь строилась прямая ссылка без авторизации с расчётом «если
 * сервер закроет неавторизованные thumbnails — откатимся на инициалы».
 * Наш Synapse их УЖЕ закрыл (authenticated media, Matrix 1.11+), так что
 * «откат» стал постоянным состоянием: аватаров не было нигде.
 * Картинку теперь тянет useAuthedAvatar через fetch с токеном.
 */
export function avatarOf(client: MatrixClient, room: Room): string | null {
  // Размеры не передаём: нужен исходный mxc://, а не thumbnail-ссылка.
  const own = room.getMxcAvatarUrl();
  if (own) return own;
  // Антон 16.09 20:38: DM-комнаты без своего аватара (Zuko, Katara, Vasya…)
  // показывали инициалы, хотя у людей аватары есть. Как в Element: для
  // комнаты на двоих берём аватар собеседника. Спейсы — только свой аватар.
  if (room.isSpaceRoom()) return null;
  const me = client.getUserId();
  const others = room.getJoinedMembers().filter((m) => m.userId !== me);
  if (others.length === 1) return others[0].getMxcAvatarUrl() ?? null;
  const inviter = room.getDMInviter();
  if (inviter) return room.getMember(inviter)?.getMxcAvatarUrl() ?? null;
  if (others.length === 0) {
    const invited = room.getMembersWithMembership("invite").filter((m) => m.userId !== me);
    if (invited.length === 1) return invited[0].getMxcAvatarUrl() ?? null;
  }
  return null;
}

function summarize(
  client: MatrixClient,
  room: Room,
  invite: boolean,
  directIds: Set<string>,
  spaceIds: string[],
): RoomSummary {
  const encrypted = isEncrypted(room);
  const name = room.name || room.roomId;
  // getDMInviter отвечает на вопрос «меня позвали в личную переписку?» и
  // работает для ещё не принятых приглашений, которых в m.direct пока нет.
  // Личная переписка. Три источника, и третий добавлен по проверке Антона
  // 10.09: комната «Momo Notifications» ощущается личной, но НЕ помечена
  // как direct — её создали приглашением без флага is_direct, и в m.direct
  // она не попала. Из-за этого правило «в 1х1 уведомляем на любое
  // сообщение» на ней не срабатывало: клиент считал её группой.
  // Запасной признак — ровно два участника и не пространство. Это то же
  // допущение, что делают Element и Telegram, когда метка отсутствует.
  // ПРАВКА (Антон, 2026-09-17 19:52): метка m.direct — необходимое, но
  // не достаточное условие. У Антона в m.direct за Dinussy числилась
  // старая комната на ТРОИХ (он + Anton + Dinussy), и список показывал
  // «Dinussy» дважды. Личка — это ровно два участника (или один, если
  // собеседник вышел). Комната с тремя и больше — группа, что бы ни
  // говорил m.direct.
  const joined = room.getJoinedMemberCount();
  const direct =
    !room.isSpaceRoom() &&
    joined <= 2 &&
    (directIds.has(room.roomId) || Boolean(room.getDMInviter()) || joined === 2);
  const { sender, text } = invite
    ? { sender: null, text: tr("room.invitePreview") }
    : previewOf(client, room, encrypted, direct);
  const counts = invite ? { total: 0, thread: 0 } : unreadOf(client, room);
  // Циферка «меня ждут» — по модели 13:49, единственное место расчёта
  // (notifyingOf). Жирный признак — «есть новое, но циферки нет».
  const notifying = invite ? 0 : notifyingOf(client, room, direct, counts.total);
  // Жирная строка — ТОЛЬКО когда циферки нет совсем: если комната уже
  // получила бейдж (notifying > 0), сигнал подан, и делать её ещё и
  // жирной значило бы показать один факт дважды. «Жирный ИЛИ бейдж».
  const hasNewMessages = !invite && counts.total > 0 && notifying === 0;

  return {
    roomId: room.roomId,
    name,
    initials: initialsOf(name),
    avatarUrl: avatarOf(client, room),
    previewSender: sender,
    previewText: text,
    lastTs: lastTsOf(room),
    unread: counts.total,
    notifying,
    hasNewMessages,
    threadUnread: counts.thread,
    highlight: invite ? 0 : highlightOf(room),
    encrypted,
    invite,
    direct,
    pinned: invite ? false : isPinned(room),
    spaceIds,
  };
}

export interface RoomLists {
  invites: RoomSummary[];
  joined: RoomSummary[];
  spaces: SpaceInfo[];
  /**
   * ЕДИНЫЙ источник счётчиков для всей навигации (заголовок вкладки,
   * рельс, фильтры). Требование Антона: цифра в заголовке «(3)» и на
   * иконках обязана совпадать — поэтому агрегаты считаются ОДИН раз здесь,
   * на пересборку списка, а UI только раздаёт готовые числа.
   */
  totals: {
    /**
     * Число ЖДУЩИХ ЧАТОВ по всей навигации: join-комнаты с notifying > 0
     * ПЛЮС каждое приглашение (= 1). Это «(3)» в заголовке вкладки.
     */
    allUnread: number;
    /**
     * Число ждущих чатов во вкладке Home (иконка Home и её вкладка):
     * личные чаты плюс комнаты вне пространств — см. isHomeRoom.
     */
    homeUnread: number;
    /** Число чатов, где меня упомянули (highlight > 0) — красный бейдж. */
    allHighlight: number;
    /**
     * Число чатов во вкладке «Непрочитанные».
     *
     * УТОЧНЕНИЕ АНТОНА (2026-09-10 14:33): «не нужно считать чаты, в которых
     * нет обращения к тебе или твоему треду». Раньше сюда входил и жирный
     * шум (hasNewMessages) — вкладка получалась шире модели «меня ждут»,
     * и цифра снова начинала означать «сколько накапало». Теперь вкладка
     * и её счётчик работают по тому же правилу notifying, что и вся
     * остальная навигация. Жирные комнаты остаются видны в общем списке
     * (Home / спейс), просто не попадают в цифры и в этот фильтр.
     */
    unreadChats: number;
  };
}

/**
 * Правило вкладки Home: личный чат ВСЕГДА, либо комната ВНЕ пространств.
 * Вынесено сюда, чтобы фильтр в RoomList и счётчик homeUnread использовали
 * ОДНО условие — продублированное в двух местах оно неминуемо разошлось бы
 * со временем.
 *
 * Личные чаты остаются в Home, даже когда формально лежат внутри спейса:
 * прямое решение Антона 2026-09-22 09:14 UTC («В Хоме входят только личные
 * чаты и те что не принадлежат спейсам», подтверждено отдельно). До этой
 * правки условие было только `spaceIds.length === 0`, и личный чат,
 * положенный в спейс, из Home пропадал. Нативный iOS-клиент ведёт себя так
 * же (коммит 9d37fae) — расхождение между клиентами было бы дефектом.
 */
export function isHomeRoom(r: RoomSummary): boolean {
  return r.direct || r.spaceIds.length === 0;
}

/** Собрать списки приглашений и присоединённых комнат, отсортированные по времени. */
export function buildRoomLists(client: MatrixClient): RoomLists {
  const invites: RoomSummary[] = [];
  const joined: RoomSummary[] = [];
  // Считаем один раз на пересборку списка, а не на каждую комнату.
  const directIds = directRoomIds(client);
  const memberOf = spaceMembership(client);
  const spaceRooms: Room[] = [];

  for (const room of client.getRooms()) {
    const membership = room.getMyMembership();
    if (membership === "invite") {
      invites.push(summarize(client, room, true, directIds, []));
    } else if (membership === "join") {
      // Пространства (m.space) — не чаты, в списке переписок им не место.
      // Но именно они дают вкладки в верхнем ряду, поэтому собираем отдельно.
      if (room.isSpaceRoom()) {
        spaceRooms.push(room);
        continue;
      }
      joined.push(
        summarize(client, room, false, directIds, [...(memberOf.get(room.roomId) ?? [])]),
      );
    }
  }

  // Закреплённые — наверх, внутри каждой группы по времени последнего события.
  joined.sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.lastTs - a.lastTs);
  invites.sort((a, b) => b.lastTs - a.lastTs);

  const spaces: SpaceInfo[] = spaceRooms
    .map((space) => {
      // Комнаты пространства считаем ОДИН раз и раздаём производные цифры.
      // Модель 13:49 считала unreadRooms по notifying (для групповых комнат —
      // только упоминания). Антон 16.09 20:55: «непрочитанное в спейсе ALG не
      // отображается счётчиком» — бейдж спейса = ЧИСЛО ЧАТОВ с любым
      // непрочитанным (unread), красный бейдж = чаты с упоминаниями.
      const inSpace = joined.filter((r) => r.spaceIds.includes(space.roomId));
      return {
        roomId: space.roomId,
        name: space.name || space.roomId,
        initials: spaceLetterOf(space.name || space.roomId),
        avatarUrl: avatarOf(client, space),
        unreadRooms: inSpace.filter((r) => r.unread > 0).length,
        highlight: inSpace.filter((r) => r.highlight > 0).length,
      };
    })
    // Порядок вкладок должен быть стабильным между перерисовками, иначе ряд
    // будет прыгать под курсором. getRooms() такого порядка не гарантирует.
    .sort((a, b) => a.name.localeCompare(b.name, "ru"));

  // ЕДИНЫЙ ПОДСЧЁТ (модель 13:49): все цифры навигации — числа ЧАТОВ,
  // вычисленные здесь один раз из готовых полей RoomSummary. UI только
  // раздаёт; правило «кого считать ждущим» в UI не дублируется.
  const totals = {
    allUnread: joined.filter((r) => r.notifying > 0).length + invites.length,
    homeUnread: joined.filter((r) => isHomeRoom(r) && r.notifying > 0).length,
    allHighlight: joined.filter((r) => r.highlight > 0).length,
    unreadChats: joined.filter((r) => r.notifying > 0).length,
  };

  return { invites, joined, spaces, totals };
}
